import { useToast } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiClient, useApiClient } from 'api/context-provider/useApiClient';
import { ApiError, InferFetchData, throwOnError } from 'api/helpers';
import { operations } from 'api/schema';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import { useTranslation } from 'react-i18next';

import useAutomationStatusQuery from '../useAutomationStatusQuery';

export type UpdateAutomationPayload = Omit<
  NonNullable<operations['updateAutomation']['requestBody']>['content']['application/json'],
  'status'
> & {
  id: number;
  status?: 0 | 1;
};

async function updateAutomation(apiClient: ApiClient, { id, ...body }: UpdateAutomationPayload) {
  return apiClient.PUT('/op/v2/automations/{id}', { params: { path: { id } }, body });
}

export function useUpdateAutomationStatusMutation() {
  const apiClient = useApiClient();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<InferFetchData<typeof updateAutomation>, ApiError, UpdateAutomationPayload>({
    mutationKey: ['UpdateAutomation'],
    mutationFn: (payload) => throwOnError(updateAutomation(apiClient, payload)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: useAutomationStatusQuery.getKey() });
      queryClient.invalidateQueries({ queryKey: ['AutomationActivityLog'] });
      toast({ status: 'success', description: t('pages:automations.toast.updated'), duration: 2000 });
    },
    onError: (error) => {
      manageErrorResponse(toast, error as any);
    },
  });
}
